<template>
    <app-layout>
        <template #icon>
            <div class="flex h-10 w-10 items-center justify-center">
                <CreditCard class="zap-gradient text-gray-700" :size="32" />
            </div>
        </template>
        <template #title> {{ $t("billing.subscription.title") }} </template>
        <template #description>
            {{ $t("billing.subscription.description") }}</template
        >

        <div
            class="mx-auto max-w-7xl px-4 pb-60 sm:px-6 sm:py-6 sm:pb-10 lg:px-6"
        >
            <div class="col-span-4"></div>
            <div
                v-if="!activePlan"
                class="mt-4 grid max-w-3xl grid-cols-1 gap-12 sm:mt-10"
            >
                <TariffCardFull :pricing-data="pro">
                    <PaymentModal
                        :client-secret="clientSecret"
                        :plans="plans"
                        :pricing-data="pro"
                        :billing-address="billingAddress"
                        :members-count="membersCount"
                        :teamId="team ? team.id : null"
                    >
                        <Button class="mt-4 sm:mt-0" size="lg">{{
                            $t("billing.card.subscribe")
                        }}</Button>
                    </PaymentModal>
                </TariffCardFull>
            </div>

            <div
                v-if="activePlan"
                class="mt-4 grid max-w-3xl grid-cols-1 gap-4"
            >
                <TariffCardFull
                    :pricing-data="pro"
                    :subscription="subscription"
                    :active-plan="activePlan"
                    @resume="resumeSubscription"
                    @cancel-subscription="cancelSubscription"
                    @switch-plan="updateSubscription(switchPlanId ? switchPlanId.plan_id : undefined)"
                >
                </TariffCardFull>

                <div>
                    <Button v-if="subscription" class="" variant="outline">{{
                        $t("billing.card.manage_cards")
                    }}</Button>
                </div>
            </div>

            <Tabs default-value="billing-address" class="mt-10">
                <TabsList>
                    <TabsTrigger value="billing-address">
                        {{ $t("billing.billing_information.title") }}
                    </TabsTrigger>
                    <TabsTrigger value="invoices">
                        {{ $t("billing.invoices.title") }}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="billing-address">
                    <div class="max-w-3xl">
                        <div class="mt-4 px-2">
                            <p class="text-sm text-iron-600">
                                {{
                                    $t(
                                        "billing.billing_information.description",
                                    )
                                }}
                            </p>
                        </div>
                        <BillingInformation
                            class="mt-4"
                            :billing-address="billingAddress"
                        ></BillingInformation>
                    </div>
                </TabsContent>
                <TabsContent value="invoices">
                    <div class="max-w-3xl">
                        <Invoices />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    </app-layout>
</template>

<script lang="ts" setup>
import AppLayout from "../../../../Layouts/AppLayout.vue";
import BillingInformation from "./components/BillingInformation.vue";
import route from "ziggy-js";
import Invoices from "./components/Invoices.vue";
import { computed } from "vue";
import { BillingAddress, Plan, Subscription } from "./types/";
import TariffCardFull from "./components/TariffCardIndividual.vue";
import { usePricingData } from "./composables/usePricingData";
import PaymentModal from "./components/PaymentModal.vue";
import { Button } from "@/components/ui/button";
import { router } from "@inertiajs/vue3";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import useConfirm from "../ConfirmDialog/composables/useConfirm";
import { useI18n } from "vue-i18n";
import { CreditCard } from "lucide-vue-next";
import {Team} from "@/Pages/ZapTime/App/Teams/types/Team";

const props = defineProps<{
    billingAddress: BillingAddress;
    subscription?: Subscription;
    subscribed: boolean;
    clientSecret: string;
    plans: Plan[];
    membersCount: number;
    team?: Team;
}>();

const { show } = useConfirm();
const { t } = useI18n();

const { pro } = usePricingData(props.plans);

const activePlan = computed(() => {
    return props.plans.find(
        (plan) => plan.plan_id === props.subscription?.stripe_price,
    );
});

const switchPlanId = computed(() => {
    return props.plans.find(
        (plan) => activePlan.value && plan.id === activePlan.value.id.replace(activePlan.value.recurring === 'monthly' ? 'MONTHLY' : 'YEARLY', activePlan.value.recurring === 'monthly' ? 'YEARLY' : 'MONTHLY'),
    );
});

async function updateSubscription(plan?: string) {
    if (plan === undefined) {
        return;
    }

    router.post(
        route("app.subscription.update", { team: props.team?.id }),
        {
            plan: plan,
        },
        {
            onError: (error) => {
                console.error(error);
            },
        },
    );
}

async function cancelSubscription() {
    const res = await show({
        header: t("billing.card.cancel_subscription"),
        message: t("billing.card.cancel_subscription_description"),
        confirm: t("billing.card.cancel_subscription"),
        cancel: t("billing.card.cancel_subscription_cancelation"),
    });

    if (res) {
        router.post(
            route("app.subscription.cancel", { team: props.team?.id }),
            {},
            {
                onError: (error) => {
                    console.error(error);
                },
            },
        );
    }
}

async function resumeSubscription() {
    router.post(
        route("app.subscription.resume", { team: props.team?.id }),
        {},
        {
            onError: (error) => {
                console.log(error);
            },
        },
    );
}
</script>
